import { useState } from "react";

import api from "@helpers/api";
import Settings from "@classes/Settings";

const useAddDeleteTagModal = props => {
  const {
    open,
    toggle,
    tag,
    client,
    userName,
    allPartnerTags,
    updateCustomers,
    handleTagFilterChange
  } = props;

  const [newTagName, setNewTagName] = useState("");
  const [inputSuggestions, setInputSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [lastUsedTags, setLastUsedTags] = useState(Settings.get("TAGS_LAST_USED") || []);
  const [fetching, setFetching] = useState(false);
  const [inputErrors, setInputErrors] = useState(null);
  const [serverMessage, setServerMessage] = useState(null);

  const action = tag ? "delete" : "add";
  const canSubmitForm =
    action === "add" ? !!newTagName && !inputErrors?.tagName : true;

  const handleKeyDown = e => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSuggestionIndex(prevIndex =>
        Math.min(prevIndex + 1, inputSuggestions.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSuggestionIndex(prevIndex =>
        prevIndex <= 0 ? -1 : prevIndex - 1
      );
    } else if (e.key === "Enter") {
      if (selectedSuggestionIndex >= 0) {
        const suggestion = inputSuggestions[selectedSuggestionIndex];
        const suggestionFormatted =
          suggestion.charAt(0).toUpperCase() +
          suggestion.slice(1).toLowerCase();

        setNewTagName(suggestionFormatted);
        setInputSuggestions([]);
        setSelectedSuggestionIndex(-1);
      } else {
        handleSubmit(e);
      }
    }
  };

  const handleInputChange = e => {
    const { id, value } = e.target;

    if (id === "tagName") {
      switch (true) {
        case /\s/.test(value):
          // tagName cannot contain spaces
          setInputErrors(prevErrors => ({
            ...prevErrors,
            tagName: "En tagg måste vara ett enda ord utan mellanslag."
          }));
          break;
        default:
          setInputErrors(prevErrors => ({
            ...prevErrors,
            tagName: null
          }));
          break;
      }

      // suggest existing tags that match input
      const suggestions =
        value.length > 0
          ? allPartnerTags.filter(existingTag => {
              const lowerTag = existingTag.toLowerCase();
              const lowerValue = value.toLowerCase();

              if (value.length < 3) {
                return (
                  lowerTag.startsWith(lowerValue) && lowerTag !== lowerValue
                );
              }
              return lowerTag.includes(lowerValue) && lowerTag !== lowerValue;
            })
          : [];

      setInputSuggestions(suggestions);
      setNewTagName(value);
    }
  };

  const handleSubmit = async e => {
    try {
      setFetching(true);
      const tags = await api(`client/tag/${action}`, {
        tag: tag || newTagName.toLowerCase(),
        clientId: client.client_id || client.clientId,
        userName
      });

      // get last used tags from settings
      let lastUsedTags = Settings.get("TAGS_LAST_USED") || [];
      if (action === "add") {
        // add new tag to last used tags
        lastUsedTags = [newTagName.toLowerCase(), ...lastUsedTags.filter(t => t !== newTagName.toLowerCase())];
        // keep only the 5 most recent tags
        lastUsedTags = lastUsedTags.slice(0, 5);
      }
      // save updated last used tags to settings
      Settings.set("TAGS_LAST_USED", lastUsedTags);
      setLastUsedTags(lastUsedTags);      

      setFetching(false);
      const updatedClient = { ...client, tags };
      if (handleTagFilterChange) handleTagFilterChange("all"); // reset tag filter to show all tags
      updateCustomers(updatedClient);
      return toggle(null);
    } catch (error) {
      setFetching(false);
      setServerMessage({
        type: "error",
        text: "Något gick fel. Kontrollera din internetanslutning eller kontakta supporten."
      });
      console.error(error);
    }
  };

  return {
    open,
    action,
    tag,
    fetching,
    toggle,
    newTagName,
    setNewTagName,
    inputSuggestions,
    setInputSuggestions,
    selectedSuggestionIndex,
    setSelectedSuggestionIndex,
    lastUsedTags,
    handleKeyDown,
    inputErrors,
    canSubmitForm,
    serverMessage,
    handleInputChange,
    handleSubmit
  };
};

export default useAddDeleteTagModal;
